import type { ReactNode } from 'react';
import { useMemo } from 'react';
import type { Item, MusicAlbum } from '../api.ts';
import { formatDuration, progressRatio } from '../format.ts';
import { ChevronDownIcon, MusicIcon, PlayIcon, QueueIcon, SparklesIcon } from '../Icon.tsx';
import { groupMusicLibraryItems, type MusicLibraryAlbumGroup } from '../musicLibrary.ts';
import { useSession } from '../session.tsx';
import type { ViewPrefsApi } from '../viewPrefs.ts';
import { ItemCard } from './ItemCard.tsx';
import { Cover } from './MusicCards.tsx';
import { ViewToolbar } from './ViewToolbar.tsx';

/**
 * Albüm başlığı: başlık albüm ekranını açar, yanındaki düğme düzenlemeye
 * götürür. Albüm kimliği eski sunucuda yoksa başlık düz metin kalır.
 */
function AlbumHeader({
  album,
  canEdit,
  onOpenAlbum,
}: {
  album: MusicLibraryAlbumGroup;
  canEdit: boolean;
  onOpenAlbum?: ((albumId: string) => void) | undefined;
}): ReactNode {
  const albumId = album.id;
  const open = onOpenAlbum;
  const openAlbum = albumId !== null && open !== undefined ? () => open(albumId) : null;

  return (
    <header className="music-library-album__header">
      <MusicIcon size={15} />
      <h3>
        {openAlbum === null ? (
          album.title
        ) : (
          <button type="button" className="music-library-album__open" onClick={openAlbum}>
            {album.title}
          </button>
        )}
      </h3>
      {album.year !== null && <span>{album.year}</span>}
      {openAlbum !== null && (
        <button
          type="button"
          className="button button--ghost music-library-album__edit"
          onClick={openAlbum}
          aria-label={`${album.title} albümünü ${canEdit ? 'düzenle' : 'aç'}`}
        >
          <SparklesIcon size={14} />
          {canEdit ? 'Albümü düzenle' : 'Albümü aç'}
        </button>
      )}
    </header>
  );
}

/**
 * Müzik kütüphanesinde düz kart ızgarası yerine sanatçı/albüm hiyerarşisi.
 * Sanatçı bölümleri `<details>` olduğu için varsayılan olarak kapalıdır.
 */
export function MusicLibraryGroups({
  items,
  albums,
  view,
  onWatch,
  onEnqueue,
  onOpenAlbum,
}: {
  items: Item[];
  /** Sunucudan gelen albüm künyeleri; başlık/kapak ve düzenleme bağlantısı. */
  albums?: readonly MusicAlbum[] | undefined;
  view: ViewPrefsApi;
  onWatch: (itemId: string) => void;
  onEnqueue?: ((item: Item) => void) | undefined;
  onOpenAlbum?: ((albumId: string) => void) | undefined;
}): ReactNode {
  const { user } = useSession();
  const canEdit = user?.role === 'admin';
  const groups = useMemo(() => groupMusicLibraryItems(items, albums ?? []), [items, albums]);

  return (
    <section className="music-library" aria-label="Sanatçıya göre müzik kütüphanesi">
      <div className="music-library__header">
        <div>
          <h2 className="music-library__title">Sanatçılar</h2>
          <p className="music-library__subtitle">
            {groups.length} sanatçı · {items.length} parça
            {view.isList
              ? canEdit
                ? ' · albümü düzenlemek için sanatçıyı açın'
                : ' · açmak için sanatçıya dokunun'
              : ''}
          </p>
        </div>
        <div className="music-library__header-actions">
          <span className="section__badge">{items.length} parça</span>
          <ViewToolbar view={view} />
        </div>
      </div>

      {view.isList ? (
        <div className="music-library__groups">
          {groups.map((artist) => (
            <details className="music-library-artist" key={artist.key}>
              <summary className="music-library-artist__summary">
                <span className="music-library-artist__cover">
                  <Cover
                    itemId={artist.coverItemId}
                    thumbnailUrl={artist.coverThumbnailUrl}
                    alt={`${artist.name} kapağı`}
                  />
                </span>
                <span className="music-library-artist__meta">
                  <strong>{artist.name}</strong>
                  <small>
                    {artist.albumCount} albüm · {artist.trackCount} parça
                  </small>
                </span>
                <ChevronDownIcon size={20} className="music-library-artist__chevron" />
              </summary>

              <div className="music-library-artist__body">
                {artist.albums.map((album) => (
                  <section className="music-library-album" key={album.key}>
                    <AlbumHeader album={album} canEdit={canEdit} onOpenAlbum={onOpenAlbum} />

                    <ol className="music-track-list" aria-label={`${album.title} parçaları`}>
                      {album.tracks.map((item, index) => {
                        const ratio = progressRatio(
                          item.resumePositionMs ?? item.progress?.positionMs,
                          item.durationMs,
                        );
                        return (
                          <li className="music-library-track" key={item.id}>
                            <button
                              type="button"
                              className="music-library-track__main"
                              onClick={() => onWatch(item.id)}
                            >
                              <span className="music-library-track__number">
                                {item.music?.trackNumber ?? index + 1}
                              </span>
                              <span className="music-library-track__title">{item.title}</span>
                              <span className="music-library-track__progress">
                                {ratio > 0 ? `%${Math.round(ratio * 100)}` : ''}
                              </span>
                              <span className="music-library-track__duration">
                                {formatDuration(item.durationMs)}
                              </span>
                              <PlayIcon size={14} />
                            </button>
                            {onEnqueue !== undefined && (
                              <button
                                type="button"
                                className="music-library-track__queue"
                                onClick={() => onEnqueue(item)}
                                aria-label={`${item.title} parçasını sıraya ekle`}
                                title="Sıraya ekle"
                              >
                                <QueueIcon size={15} />
                              </button>
                            )}
                          </li>
                        );
                      })}
                    </ol>
                  </section>
                ))}
              </div>
            </details>
          ))}
        </div>
      ) : (
        <div className="music-library__grid-groups">
          {groups.map((artist) => (
            <section className="music-library-grid-artist" key={artist.key}>
              <header className="music-library-grid-artist__header">
                <span className="music-library-grid-artist__cover">
                  <Cover
                    itemId={artist.coverItemId}
                    thumbnailUrl={artist.coverThumbnailUrl}
                    alt={`${artist.name} kapağı`}
                  />
                </span>
                <div>
                  <strong>{artist.name}</strong>
                  <small>
                    {artist.albumCount} albüm · {artist.trackCount} parça
                  </small>
                </div>
              </header>
              <div className="music-library-grid-albums">
                {artist.albums.map((album) => (
                  <section className="music-library-grid-album" key={album.key}>
                    <AlbumHeader album={album} canEdit={canEdit} onOpenAlbum={onOpenAlbum} />
                    <div className={`grid ${view.gridClassName}`}>
                      {album.tracks.map((item) => (
                        <ItemCard
                          key={item.id}
                          item={item}
                          layout="square"
                          mediaKind="music"
                          href={`#/oge/${item.id}`}
                          onClick={() => onWatch(item.id)}
                        />
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </section>
  );
}
